import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, KeyRound, AlertCircle, ShieldCheck } from 'lucide-react';

/**
 * SetPassword — landing for invite / recovery flows.
 *
 * Three entry shapes are supported. Supabase-delivered links require the
 * destination to be present in the project's Auth redirect allowlist.
 *
 *   1. App-owned link (used by our own Emergent-Resend invite emails):
 *        /imposta-password?token_hash=<hashed_token>&type=invite|recovery
 *      → we call `supabase.auth.verifyOtp({ token_hash, type })` ourselves,
 *        which establishes the session with zero dashboard configuration.
 *
 *   2. Legacy hash-fragment session (from Supabase's own SMTP-sent emails,
 *      kept as a fallback so nothing breaks if allowlist ever gets fixed):
 *        /imposta-password#access_token=...&type=invite
 *      → explicitly restore the session here; the shared client deliberately
 *        keeps `detectSessionInUrl` disabled.
 *
 *   3. PKCE callback: /imposta-password?code=...
 *      → exchange using the verifier saved by the initiating browser.
 */
export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [expired, setExpired] = useState(false);
  // Prevent double-verify in dev StrictMode (verifyOtp is single-use).
  const verifyStarted = useRef(false);
  const codeExchange = useRef(null);

  useEffect(() => {
    const hash = window.location.hash || '';
    if (/error=access_denied/i.test(hash) || /otp_expired/i.test(hash)) {
      setExpired(true); setReady(true); return;
    }
    let cancelled = false;
    const fragment = new URLSearchParams(hash.replace(/^#/, ''));
    const fragmentAccessToken = fragment.get('access_token');
    const fragmentRefreshToken = fragment.get('refresh_token');
    const fragmentType = fragment.get('type');
    if (fragmentAccessToken || fragmentRefreshToken) {
      // Remove credentials from the address bar without sending them anywhere.
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }

    const tokenHash = searchParams.get('token_hash');
    const code = searchParams.get('code');
    const type = (searchParams.get('type') || 'invite').toLowerCase();

    (async () => {
      if (code) {
        // Never fall back to an unrelated existing session on exchange failure.
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        window.history.replaceState(window.history.state, '', cleanUrl.pathname + cleanUrl.search);
        try {
          if (!codeExchange.current || codeExchange.current.code !== code) {
            codeExchange.current = { code, promise: supabase.auth.exchangeCodeForSession(code) };
          }
          const { data, error } = await codeExchange.current.promise;
          if (cancelled) return;
          if (error || !data?.session) {
            setHasSession(false); setExpired(true); setReady(true); return;
          }
          setHasSession(true); setReady(true);
        } catch {
          if (!cancelled) {
            setHasSession(false); setExpired(true); setReady(true);
          }
        }
        return;
      }
      if (fragmentAccessToken || fragmentRefreshToken) {
        if (!fragmentAccessToken || !fragmentRefreshToken
          || !['recovery', 'invite'].includes(fragmentType)) {
          setExpired(true); setReady(true); return;
        }
        const { data, error } = await supabase.auth.setSession({
          access_token: fragmentAccessToken,
          refresh_token: fragmentRefreshToken,
        });
        if (cancelled) return;
        if (error || !data?.session) {
          setExpired(true); setReady(true); return;
        }
        setHasSession(true);
        setReady(true);
        return;
      }
      // App-owned link → verify the OTP ourselves.
      if (tokenHash && !verifyStarted.current) {
        verifyStarted.current = true;
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,   // 'invite' | 'recovery' | 'magiclink'
        });
        if (cancelled) return;
        if (error) {
          // Expired / already used / tampered → clear expired UI.
          setExpired(true); setReady(true); return;
        }
        setHasSession(!!data?.session);
        setReady(true);
        return;
      }
      // Fallback path: session may already have been picked up from the hash.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setHasSession(!!data.session);
      setReady(true);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('La password deve avere almeno 8 caratteri.'); return; }
    if (password !== confirm) { setErr('Le due password non coincidono.'); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' });
      if (signOutError) throw signOutError;
      window.history.replaceState({}, '', window.location.pathname);
      navigate('/', { replace: true, state: { passwordUpdated: true } });
    } catch (e2) {
      setErr(e2.message || 'Impossibile impostare la password. Riprova.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-app-background flex items-center justify-center px-5 py-10" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-7 sm:p-9">
          <div className="flex justify-center mb-6">
            <img src="/brand/restaurantos-logo-horizontal.svg" alt="RestaurantOS" className="h-12 sm:h-14 w-auto" />
          </div>
          <div className="flex items-center gap-2 text-brand-green text-sm font-semibold mb-4">
            <ShieldCheck className="w-4 h-4" /> Accesso protetto
          </div>

          {!ready && (
            <div className="flex items-center gap-2 text-stone-500" data-testid="set-password-verifying"><Loader2 className="w-4 h-4 animate-spin" /> Verifica invito…</div>
          )}

          {ready && expired && (
            <div className="space-y-4" data-testid="set-password-expired">
              <h1 className="font-display text-3xl font-bold text-brand-navy leading-tight">Questo invito non è più valido</h1>
              <p className="text-stone-500 text-sm">Il link potrebbe essere scaduto o già utilizzato. Chiedi al tuo amministratore di reinviare l'invito.</p>
              <Button onClick={() => navigate('/')} className="w-full bg-brand-green hover:bg-brand-green-hover text-white" data-testid="set-password-back-login">Torna al login</Button>
            </div>
          )}

          {ready && !expired && !hasSession && (
            <div className="space-y-4" data-testid="set-password-nosession">
              <h1 className="font-display text-3xl font-bold text-brand-navy leading-tight">Link non valido</h1>
              <p className="text-stone-500 text-sm">Apri il link direttamente dall'email di invito che hai ricevuto da RestaurantOS.</p>
              <Button onClick={() => navigate('/')} className="w-full bg-brand-green hover:bg-brand-green-hover text-white">Torna al login</Button>
            </div>
          )}

          {ready && hasSession && (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy leading-tight">Crea la tua password</h1>
              <p className="mt-2 text-stone-500 text-sm">Scegli una password sicura. Dopo la conferma entrerai direttamente in RestaurantOS.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="set-password-form">
                <div>
                  <Label htmlFor="pw" className="text-stone-700 text-sm font-medium">Nuova password</Label>
                  <Input id="pw" type="password" required autoComplete="new-password" minLength={8}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="set-password-input" className="mt-1.5 h-11" placeholder="Minimo 8 caratteri" />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-stone-700 text-sm font-medium">Conferma password</Label>
                  <Input id="pw2" type="password" required autoComplete="new-password" minLength={8}
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="set-password-confirm" className="mt-1.5 h-11" />
                </div>
                {err && (
                  <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="set-password-error">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{err}</span>
                  </div>
                )}
                <Button type="submit" disabled={busy} className="w-full h-12 text-base font-semibold bg-brand-green hover:bg-brand-green-hover text-white gap-2 shadow-sm" data-testid="set-password-submit">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio…</> : <><KeyRound className="w-4 h-4" /> Continua</>}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
